'use client'
import { Icons } from '@/app/components/ui/Icons'
import { FeatureFlag, useFeatureFlags } from '@/app/hooks/useFeatureFlags'
import classNames from 'classnames'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { useParams } from 'next/navigation'
import { FC, useMemo } from 'react'
import {
	emitBlogLinkClickGAEvent,
	emitContactUsClickGAEvent,
	emitPrivacyPolicyClickGAEvent,
	emitTermOfUseClickGAEvent,
} from '../ga-event-helpers'

interface FooterNavItem {
	href: string
	title: string
	onClick?: () => void
	featureFlag?: FeatureFlag
}

const FOOTER_NAV_ITEMS: FooterNavItem[] = [
	{
		href: '/contact-us',
		title: 'contactUs',
		onClick() {
			emitContactUsClickGAEvent()
		},
	},
	{
		href: '/terms',
		title: 'terms',
		onClick() {
			emitTermOfUseClickGAEvent()
		},
	},
	{
		href: '/privacy-policy',
		title: 'privacyPolicy',
		onClick() {
			emitPrivacyPolicyClickGAEvent()
		},
	},
	{
		href: '/blog',
		title: 'blog',
		featureFlag: FeatureFlag.BLOG,
		onClick() {
			emitBlogLinkClickGAEvent()
		},
	},
]

const FooterNav: FC = () => {
	const t = useTranslations('Footer')
	const params = useParams()
	const { isEnabled } = useFeatureFlags()
	const locale = params.lang as string

	const visibleNavItems = useMemo(
		() =>
			FOOTER_NAV_ITEMS.filter(
				(item) => !item.featureFlag || isEnabled(item.featureFlag, locale)
			),
		[isEnabled, locale]
	)

	return (
		<nav className="footer-nav">
			<ul>
				{visibleNavItems.map((item, i) => (
					<li
						key={i}
						className="flex max-md:flex-col max-md:items-start items-center"
					>
						<div className="nav-item flex justify-center items-center w-full">
							<Link
								href={`/${params.lang}${item.href}`}
								aria-label={t(item.title)}
								onClick={() => item.onClick?.()}
								className={classNames(
									// Text styles
									'text-text-900 font-bold leading-5',
									// Padding and sizing
									'rtl:flex-row-reverse',
									'py-3 lg:py-2',
									'max-md:text-left',
									'rtl:max-md:text-right',
									'max-md:w-full',
									// Flexbox (for larger screens)
									'max-md:flex max-md:items-center max-md:justify-between',
									'md:text-center'
								)}
							>
								{t(item.title)}
								<Icons.chevronSmall className="md:hidden" />
							</Link>
						</div>
						{/* Mobile divider */}
						<div className="w-full h-[1px] bg-text-400 my-1 md:hidden" />
						{/* Desktop divider */}
						{i !== visibleNavItems.length - 1 && (
							<div className="hidden md:flex items-center px-0.5 self-center">
								<div className="w-[1px] h-9 bg-text-400" />
							</div>
						)}
					</li>
				))}
			</ul>
		</nav>
	)
}

export default FooterNav
